/**
 * Dialogue Lines Data Source
 */

import type { 
  DataSourceModule, 
  FieldMetadata, 
  ColumnDefinition, 
  ListWidgetItem,
  TableDataOutput,
  DataSourceConfig 
} from '../types';
import { dialogue_line } from "@/types-games";
import { formatDate } from '../shared/formatters';

// Field definitions with metadata
const fields: Record<string, FieldMetadata> = {
  id: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  source_text: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: true,
    required: true,
  },
  context: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: false,
  },
  section: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  abbreviation: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: true,
  },
  status: {
    type: 'string',
    editable: 'dropdown',
    filterable: true,
    sortable: true,
    options: [
      { label: 'Draft', value: 'draft' },
      { label: 'Review', value: 'review' },
      { label: 'Approved', value: 'approved' }
    ]
  },
  created_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  },
  updated_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  }
};

// Column definitions
const columns: ColumnDefinition[] = [
  { key: 'source_text', label: 'Text', filterable: true, metadata: fields.source_text },
  { key: 'context', label: 'Context', filterable: true, metadata: fields.context },
  { key: 'section', label: 'Section', filterable: true, metadata: fields.section },
  { key: 'abbreviation', label: 'Abbreviation', align: 'left', metadata: fields.abbreviation },
  { key: 'status', label: 'Status', align: 'left', metadata: fields.status },
  { key: 'created_at', label: 'Created', align: 'left', metadata: fields.created_at },
  { key: 'updated_at', label: 'Updated', align: 'left', metadata: fields.updated_at }
];

// Configuration
const config: DataSourceConfig = {
  type: 'dialogue-lines',
  title: 'Dialogue Lines',
  description: 'Dialogue lines from the current game',
  expandable: false,
};

// Data fetcher (dialogue lines use strings data, pass them in)
function fetchData(selectedGame?: any, selectedFolderId?: string | null, stringsItems?: dialogue_line[]): dialogue_line[] {
  return stringsItems || [];
}

// Transformers
function toList(rawData: dialogue_line[]): ListWidgetItem[] {
  return rawData.map(line => ({
    name: line.source_text || 'No text',
    tags: 'Dialogue',
    type: line.section || 'Dialogue',
    created_at: line.created_at,
    data: { context: line.context, abbrev: line.abbrev, status: line.status },
    updated_at: line.updated_at,
    description: line.context || 'No description'
  }));
}

function toTable(rawData: dialogue_line[]): TableDataOutput {
  return {
    columns,
    data: rawData.map(line => ({
      id: line.id,
      source_text: String(line.source_text || 'No text'),
      context: String(line.context || 'No context'),
      section: String(line.section || 'No section'),
      abbreviation: String(line.abbrev || 'No abbreviation'),
      status: String(line.status || 'No status'),
      created_at: formatDate(line.created_at),
      updated_at: formatDate(line.updated_at)
    })),
    metadata: {
      totalCount: rawData.length,
      filteredCount: rawData.length
    }
  };
}

// Export the module
export const dialogueLinesDataSource: DataSourceModule = {
  type: 'dialogue-lines',
  config,
  fields,
  columns,
  fetchData,
  transformers: {
    toList,
    toTable
  }
};

